import React from 'react'
import Icon from '@ant-design/icons'
import { AntdTableEmptyPlaceholder } from '@pushly/aqe/lib/components'
import { ICON_ASSIGNMENTS, SystemIcon } from '@pushly/aqe/lib/icons'
import type { SlotId, SystemIconProps } from '@pushly/aqe/lib/icons'

export type FeatureIconProps = Omit<SystemIconProps, 'name'> & {
    /** The menu slot whose icon stands for this feature, so the page matches its menu item. */
    slot: SlotId
}

/** The Pushly icon assigned to a feature's menu item, for use on that feature's own pages. */
export const FeatureIcon = ({ slot, ...props }: FeatureIconProps) => {
    const name = ICON_ASSIGNMENTS[slot]
    return name ? <SystemIcon name={name} {...props} /> : null
}

// One component per slot, so antd's Icon keeps the same component between renders.
const anticonComponents = new Map<SlotId, React.ComponentType<React.SVGProps<SVGSVGElement>>>()

export type FeatureAnticonProps = Omit<React.ComponentProps<typeof Icon>, 'component' | 'ref'> & { slot: SlotId }

/**
 * The feature's icon inside antd's Icon wrapper (`span.anticon`), for places whose styles are
 * written around antd icons: badges, avatars, tab bars, label prefixes.
 */
export const FeatureAnticon = ({ slot, ...props }: FeatureAnticonProps) => {
    let component = anticonComponents.get(slot)
    if (!component) {
        component = (svgProps) => <FeatureIcon slot={slot} {...(svgProps as Omit<FeatureIconProps, 'slot'>)} />
        anticonComponents.set(slot, component)
    }
    return <Icon component={component} {...props} />
}

export type FeatureEmptyStateProps = Omit<
    React.ComponentProps<typeof AntdTableEmptyPlaceholder>,
    'icon' | 'iconVariant'
> & {
    /** The menu slot of the feature the empty table belongs to. */
    slot: SlotId
}

/** An empty feature table: the feature's menu icon in a chip, over the table's message. */
export const FeatureEmptyState = ({ slot, ...props }: FeatureEmptyStateProps) => (
    <AntdTableEmptyPlaceholder iconVariant="chip" icon={<FeatureIcon slot={slot} />} {...props} />
)
